import {
  IBDHostDevice,
  IBDSiteHostDevice,
  UbiquitiHost,
  UbiquitiSite,
  UbiquitiSiteWan,
} from './types';

export interface UbiquitiHostGroup {
  host: UbiquitiHost;
  sites: UbiquitiSite[] | null;
  devices: IBDHostDevice[];
}

export interface UbiquitiSiteGroup {
  site: UbiquitiSite;
  host: UbiquitiHost;
  devices: IBDSiteHostDevice[];
}

// The API returns one row per (device, host) pair — group back into one
// entry per host for a host-centric card view.
export const groupDevicesByHost = (
  devices: IBDHostDevice[],
): UbiquitiHostGroup[] => {
  if (devices === null || devices.length === 0) {
    return [];
  }

  const groups = new Map<string, UbiquitiHostGroup>();

  devices.forEach((item) => {
    const existing = groups.get(item.host.id);
    if (existing) {
      existing.devices.push(item);
    } else {
      groups.set(item.host.id, {
        host: item.host,
        sites: item.sites,
        devices: [item],
      });
    }
  });

  return Array.from(groups.values());
};

// The API returns one row per (device, host, site) pair — group back into
// one entry per site for a site-centric card view.
export const groupDevicesBySite = (
  devices: IBDSiteHostDevice[],
): UbiquitiSiteGroup[] => {
  if (devices === null || devices.length === 0) {
    return [];
  }

  const groups = new Map<string, UbiquitiSiteGroup>();

  devices.forEach((item) => {
    const existing = groups.get(item.site.siteId);
    if (existing) {
      existing.devices.push(item);
    } else {
      groups.set(item.site.siteId, {
        site: item.site,
        host: item.host,
        devices: [item],
      });
    }
  });

  return Array.from(groups.values());
};

// site.statistics.ispInfo/percentages.wanUptime describe a fixed WAN slot
// (appears to default to whichever port is "WAN1"), not necessarily the
// connection actually carrying traffic — on some hosts the primary/active
// ISP sits on WAN2 instead, while WAN1 is down with 0% uptime. Read the
// per-WAN entries in statistics.wans instead, and use whichever one(s)
// actually have ISP info populated (an inactive/unprovisioned WAN port
// reports no ispInfo.name at all).
export const getActiveWans = (site: UbiquitiSite): UbiquitiSiteWan[] => {
  if (site.statistics.wans === null) {
    return [];
  }

  return Object.values(site.statistics.wans).filter(
    (wan) => wan.ispInfo.name !== '',
  );
};

// controllers is null (not []) for hosts that haven't fully reported back
// yet (e.g. invited-but-not-adopted) — Go marshals a nil slice as JSON null.
export const getInstalledApps = (host: UbiquitiHost): string[] => {
  if (host.reportedState.controllers === null) {
    return [];
  }

  return host.reportedState.controllers
    .filter((controller) => controller.isInstalled)
    .map((controller) => controller.name);
};
